import { Button } from "@/components/ui/button";
import { DiagnosticField } from "../echoflicks/journey/DiagnosticFields";
import { groupStats } from "./groupStats";
import { duration, label, timestamp } from "./format";
import type { QueueRunDetail } from "./types";

export function PipelineGroupInspector({
  group,
  detail,
  expand,
}: {
  group: string;
  detail: QueueRunDetail;
  expand: (group: string) => void;
}) {
  const stages = detail.items.filter(
    (stage) => stage.stage_id.split(".")[0] === group,
  );
  const stats = groupStats(stages, detail.as_of);
  return (
    <aside
      aria-label="Group inspector"
      className="space-y-4 rounded-lg border p-4 text-sm"
    >
      <h3 className="font-semibold">
        {detail.definition.groups.find((value) => value.id === group)?.label ??
          group}
      </h3>
      <p className="text-muted-foreground">
        {stages.length} observed steps in this group.
      </p>
      <dl className="grid grid-cols-2 gap-3">
        <DiagnosticField label="First start">
          {timestamp(stats.startedAt)}
        </DiagnosticField>
        <DiagnosticField label="Observed wall time">
          {duration(stats.observedWallMs)}
        </DiagnosticField>
        {Object.entries(stats.states).map(([state, count]) => (
          <DiagnosticField key={state} label={label(state)}>
            {count}
          </DiagnosticField>
        ))}
      </dl>
      <p className="text-xs text-muted-foreground">
        Overlapping observed intervals count once.
        {stats.incomplete || detail.next_cursor
          ? " Timing coverage is partial."
          : ""}
      </p>
      <p className="text-muted-foreground">
        Expand the group to compare each step with its own duration baseline.
      </p>
      <section>
        <h4 className="font-medium">Input references</h4>
        {stats.inputs.length ? (
          <ul>
            {stats.inputs.map((reference) => (
              <li className="break-all font-mono text-xs" key={reference}>
                {reference}
              </li>
            ))}
          </ul>
        ) : (
          <p>No evidence reference recorded.</p>
        )}
      </section>
      <Button
        variant="outline"
        disabled={!stages.length}
        onClick={() => expand(group)}
      >
        Expand this group
      </Button>
    </aside>
  );
}
